import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CallIntelUtils } from '@frontend/api-call-intel';
import { navigateToUrl } from '@frontend/auth';
import { hasACL, CoreACLs, type PortalUser, setLastVisitedPage } from '@frontend/auth-helpers';
import { BRIDGELINE_PATH } from '@frontend/bridgeline-tendlc';
import { useRemoveAppLoader } from '@frontend/document';
import { ErrorBoundary, MainBoundary } from '@frontend/error-boundary';
import { INTAKE_FORM_PATH_V2 } from '@frontend/onboarding';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { ShellTitleBar } from '@frontend/shell-theme';
import { shell } from '@frontend/shell-utils';
import { WEAVE_COPILOT_PATH } from '@frontend/weave-copilot';
import { Outlet, router, RootRoute } from '@frontend/weave-router';
import { AuthenticatedApp } from '../components/authenticated-app';
import { EnvironmentSwitcher } from '../components/environment-switcher';
import { GlobalSearchView } from '../components/global-search';
import { HistoryControl } from '../components/history-control/history-control';
import { useDocumentTitle } from '../components/use-document-title';
import { VersionInfo } from '../components/version-info';
import { useApp } from '../providers/app.provider';
import {
  transFormAndExtractLocationIdFromPortalRoute,
  getNewRouteFromLegacyPortalRoute,
} from '../providers/routes.provider/legacy-portal-redirects';
import { getMurderThePortalRedirects } from '../providers/routes.provider/portal-redirects';
import { useThirdPartyScripts } from '../third-party-scripts/use-third-party-scripts';
import { LOCATION_REDIRECT_STORAGE_KEY } from '../utils/location-redirect';

const signInUri = '/sign-in';

const hasShellThemeEnabled = shell?.isShell && shell?.featureAvailability?.has('shell-theme');
const hasNewShellTitleBar = shell?.isShell && shell?.featureAvailability?.has('shell-title-bar-view');
const hasLegacyShellTitleBar = hasShellThemeEnabled && !hasNewShellTitleBar;

export function RootRouteComponent() {
  useRemoveAppLoader();

  const data = RootRoute.useRouteContext();
  const showEnvSwitch = hasACL('weave', CoreACLs.DEVTOOLS) || import.meta.env.MODE === 'development';
  const originalURI = window.location.pathname + (window.location.search ?? '') + (window.location.hash ?? '');

  if (originalURI && !originalURI.startsWith(signInUri)) {
    // Transform location-prefixed URLs (e.g., /<locationId>/phone/overrides)
    // through the legacy redirect pipeline before saving, so that after sign-in
    // the app navigates to the correct transformed route.
    const transformed = transformLegacyUrl(originalURI);
    if (transformed.locationId) {
      localStorage.setItem(LOCATION_REDIRECT_STORAGE_KEY, transformed.locationId);
    }
    setLastVisitedPage(transformed.path);
  }
  useThirdPartyScripts();
  return (
    <>
      {hasLegacyShellTitleBar ? <LegacyShellTitleBar /> : null}
      <VersionInfo />
      <EnvironmentSwitcher enabled={showEnvSwitch} />
      <ErrorBoundary fallback={MainBoundary}>
        <AppComponent authenticated={data.authenticated} user={data.user}>
          <Outlet />
        </AppComponent>
      </ErrorBoundary>
    </>
  );
}

const AppComponent = ({
  authenticated,
  user,
  children,
}: {
  authenticated: boolean;
  user: PortalUser | undefined;
  children: React.ReactNode;
}) => {
  const pathname = router.state.location.pathname;
  const matches = router.state.matches;
  const deepestMatch = matches?.at(-1);
  const isPublicRoute = deepestMatch?.id.startsWith('/_public');
  const isNotFound = router.hasNotFoundMatch();

  useDocumentTitle(pathname);
  const { updateAvailable, currentVersion, shouldPreventUpdate } = useApp();

  const isIntakeForm = pathname === INTAKE_FORM_PATH_V2;
  const isBridgelineRoute = pathname === BRIDGELINE_PATH;
  const isDemoUserFlag = CallIntelUtils.isDemoUser(user?.username);
  const isWeaveCopilotRoute = pathname === WEAVE_COPILOT_PATH;

  useAppUpdater({
    history: router.history,
    updateAvailable,
    enabled: !shouldPreventUpdate && !isDemoUserFlag,
    currentVersion,
  });

  const originalURI = window.location.pathname + window.location.search;
  const shouldRedirectToSignIn =
    !isNotFound && !isPublicRoute && !authenticated && originalURI !== signInUri && originalURI !== '/';
  const shouldWrapInAuthenticatedApp =
    authenticated && !isPublicRoute && !isIntakeForm && !isBridgelineRoute && !isWeaveCopilotRoute;

  if (shouldRedirectToSignIn) {
    console.info(`Hit a private route ${originalURI} without being authenticated. Redirecting to ${signInUri}`);
    // use navigateToUrl because we're navigating outside of the router context
    navigateToUrl(signInUri, { reason: 'Hit a private route without being authenticated' });
    return <p>Redirecting</p>;
  }

  if (shouldWrapInAuthenticatedApp) {
    return <AuthenticatedApp>{children}</AuthenticatedApp>;
  }

  return <>{children}</>;
};

const useAppUpdater = ({
  history,
  updateAvailable,
  enabled,
  currentVersion,
}: {
  history: typeof router.history;
  enabled: boolean;
  updateAvailable: boolean;
  currentVersion: string | undefined;
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const unsubscribe = history.subscribe(() => {
      if (enabled) {
        return;
      }
      if (!enabled && updateAvailable) {
        timeoutRef.current = setTimeout(() => {
          console.info('Refreshing app because new version is available (' + currentVersion + ')');
          window.location.reload();
        }, 20);
      }
    });

    return () => {
      unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [history, enabled, updateAvailable, currentVersion]);
};

const TitleBarGlobalSearch = () => {
  const titleBarContentRef = useRef<HTMLElement | null>(null);
  const isXsmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const showGlobalSearch = isXsmall;

  useEffect(() => {
    if (!titleBarContentRef.current) {
      const element = document.getElementById('weave-shell-title-bar-content');
      if (element) titleBarContentRef.current = element;
    }
  });

  if (!titleBarContentRef.current || showGlobalSearch) return null;
  return createPortal(<GlobalSearchView />, titleBarContentRef.current);
};

const TitleBarNavigationControls = () => {
  const titleBarNavigationRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!titleBarNavigationRef.current) {
      const element = document.getElementById('weave-shell-title-bar-buttons');
      if (element) titleBarNavigationRef.current = element;
    }
  });

  if (!titleBarNavigationRef.current) return null;
  return createPortal(<HistoryControl />, titleBarNavigationRef.current);
};

/**
 * Transforms a legacy URL path and extracts location information.
 *
 * This function processes a legacy portal route by:
 * 1. Extracting any location ID from the original path
 * 2. Transforming the route using legacy portal route mappings
 * 3. Applying redirect rules for deprecated portal routes
 * 4. Constructing the final path with pathname, hash, and search parameters
 *
 * @param originalPath - The original legacy URL path to transform
 * @returns An object containing the transformed path and optional location ID
 * @returns {string} path - The final transformed URL path (pathname + hash + search), or the original path if no transformations apply
 * @returns {string | undefined} locationId - The extracted location ID from the route, if present
 *
 * @example
 * const result = transformLegacyUrl('/38fd93c3-78d0-4ef4-9466-99421eccf600/phone/overrides');
 * // Returns { path: '/home/dashboard#settings/phone/overrides', locationId: '38fd93c3-78d0-4ef4-9466-99421eccf600' }
 */
export function transformLegacyUrl(originalPath: string): {
  path: string;
  locationId: string | undefined;
} {
  const { locationId, transformedRoute } = transFormAndExtractLocationIdFromPortalRoute(originalPath);

  const legacyRedirect = transformedRoute ? getNewRouteFromLegacyPortalRoute(transformedRoute) : null;
  const portalRedirect = getMurderThePortalRedirects(legacyRedirect ?? originalPath);
  const finalRedirect = portalRedirect || legacyRedirect;

  if (finalRedirect) {
    // Use window.location.origin as the base URL for parsing
    // We only care about extracting pathname, hash, and search params
    const tempUrl = new URL(finalRedirect, window.location.origin);
    let path = tempUrl.pathname;
    if (tempUrl.hash) {
      path += tempUrl.hash;
    }
    if (tempUrl.search) {
      path += tempUrl.search;
    }
    return { path, locationId };
  }

  return { path: originalPath, locationId };
}

const LegacyShellTitleBar = () => {
  return (
    <>
      <ShellTitleBar />
      <TitleBarNavigationControls />
      <TitleBarGlobalSearch />
    </>
  );
};
